/* eslint-disable arrow-body-style */
import { useMutation } from '@apollo/client';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faEdit, faEnvelope, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { FC, useState } from 'react';
import Button from '../../../../components/form/button';
import Unbutton from '../../../../components/form/button/unbutton';
import Loader from '../../../../components/loader';
import ModalConfirm from '../../../../components/modal/confirm';
import Modal from '../../../../components/modal/default';
import { SEND_CLINICIAN_INVITE } from '../../../../graphql/mutation/sendClinicianInvite';
import {
  DEMOTE_TO_REGULAR_CLINICIAN,
  PROMOTE_TO_SUPER_CLINICIAN,
  USERS_CLINICIAN_DELETE,
} from '../../../../graphql/mutation/users';
import useAuth from '../../../../hooks/auth';
import useToaster from '../../../../hooks/toast';
import { GraphQLResponse, IUser } from '../../../../types';
import { logger } from '../../../../utils/logger';
import styles from './styles.module.css';

interface IUserRow {
  user: IUser;
  refresh: () => void;
}

const Clinician: FC<IUserRow> = props => {
  const { user, refresh } = props;
  const clinicianId = user.acl?.clinicianId;
  if (!clinicianId) {
    throw new Error('Clinician ID is required for Clinician component, user must be a clinician');
  }


  const { id, email, nameFirst, nameLast, role } = user;

  const isSuper = role.name === 'clinicianadmin';

  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const { user: currentUser } = useAuth();
  const { success, danger } = useToaster();

  const [del, { loading: loadingDel }] = useMutation<
    GraphQLResponse<'deleteUserClinician', string>
  >(USERS_CLINICIAN_DELETE, { errorPolicy: 'all' });

  const [promote, { loading: loadingPromote }] = useMutation<
    GraphQLResponse<'promoteToSuperClinician', IUser>
  >(PROMOTE_TO_SUPER_CLINICIAN);

  const [demote, { loading: loadingDemote }] = useMutation<
    GraphQLResponse<'demoteToRegularClinician', IUser>
  >(DEMOTE_TO_REGULAR_CLINICIAN);

  const [resendInvite, { loading: loadingResendInvite }] = useMutation<
    GraphQLResponse<'sendClinicianInvite', IUser>
  >(SEND_CLINICIAN_INVITE, { errorPolicy: 'all' });


  const handleShowEdit = () => {
    setIsOpenEdit(true);
  };

  const handleResendInvite = () => {
    resendInvite({ variables: { clinicianId: user.acl.clinicianId } }).then(res => {
      if (!res.errors?.length) {
        success('Account activation email sent successfully');
      } else {
        const errorMessage = res.errors.map(e => e.message).join('. ');
       danger(`Error: ${errorMessage}` || 'Sorry, an unknown error occurred');
      }
    });
  };

  const handleCloseEdit = () => {
    setIsOpenEdit(false);
  };

  const handleShowConfirm = () => {
    setIsOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setIsOpenConfirm(false);
  };

  const handleConfirmDelete = () => {
    setIsOpenConfirm(false);
    del({ variables: { userId: id } })
      .then(res => {
        if (res.errors && res.errors.length > 0) {
          const errorMessage = res.errors.map(e => e.message).join('. ');
          danger(`Error: ${errorMessage}`);
          return;
        }
        success('Clinician deleted successfully');
        refresh();
      })
      .catch((err: any) => {
        danger('Sorry, an unknown error occurred');
        logger.error(new Error(`Error deleting user: ${err.message}`));
      });
  };

  const handleDemote = () => {
    setIsOpenEdit(false);
    demote({ variables: { clinicianId } })
      .then(res => {
        if (res.errors && res.errors.length > 0) {
          const errorMessage = res.errors.map(e => e.message).join('. ');
          danger(`Error: ${errorMessage}`);
          return;
        }
        success('Clinician updated successfully');
        refresh();
      })
      .catch(err => {
        danger('Sorry, an unknown error occurred');
        logger.error(new Error(`Error demoting user: ${err.message}`));
      });
  };

  const handlePromote = () => {
    setIsOpenEdit(false);
    promote({ variables: { clinicianId } })
      .then(res => {
        if (res.errors && res.errors.length > 0) {
          const errorMessage = res.errors.map(e => e.message).join('. ');
          danger(`Error: ${errorMessage}`);
          return;
        }
        success('Clinician updated successfully');
        refresh();
      })
      .catch(err => {
        danger('Sorry, an unknown error occurred');
        logger.error(new Error(`Error promoting user: ${err.message}`));
      });
  };

  return (
    <div className={styles.row}>
      <div className={styles.rowLevel}>
        <div className={styles.rowLeft}>
          <div className={styles.rowItem}>
            <span className={styles.label}>
              {nameFirst} {nameLast}
            </span>
          </div>
          <div className={clsx(styles.rowItem, styles.textMuted)}>{email}</div>
        </div>
        <div className={styles.rowRight}>
          <div className={clsx(styles.rowItem, styles.textMuted)}>{role.nameDisplay}</div>

          {/* don't show buttons for current user */}
          {user.id !== currentUser?.userId && (
            <div className={styles.rowMenu}>
              <Unbutton onClick={handleShowEdit}>
                <FontAwesomeIcon icon={faEdit as IconProp} size="lg" className={styles.edit} />
              </Unbutton>

              <Unbutton onClick={handleResendInvite}>
                <FontAwesomeIcon icon={faEnvelope as IconProp} size="lg" className={styles.edit} />
              </Unbutton>

              <Unbutton onClick={handleShowConfirm}>
                <FontAwesomeIcon
                  icon={faTimesCircle as IconProp}
                  size="lg"
                  className={styles.delete}
                />
              </Unbutton>
            </div>
          )}
        </div>
      </div>

      <Loader loading={loadingPromote || loadingDel || loadingResendInvite} />

      <ModalConfirm
        title="This action can't be undone"
        isOpen={isOpenConfirm}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmDelete}
        confirmText="Delete">
        <p className={styles.modalBodyText}>
          Are you sure you want to permanently delete this clinician?
        </p>
      </ModalConfirm>

      <Modal
        isOpen={isOpenEdit}
        onClose={handleCloseEdit}
        title="Edit Clinician Permissions"
        contentProps={{
          style: {
            padding: '1.5rem 0 1.5rem 2.5rem',
            flexDirection: 'column',
          },
        }}
        style={{
          content: {
            width: '80%',
            maxWidth: '950px',
          },
        }}>
        <div>
          <h2 className="is-size-5">
            {nameFirst && nameLast ? `${nameFirst} ${nameLast}` : email} {isSuper ? 'is' : 'is not'}{' '}
            a Super Clinician
          </h2>

          <p className={styles.paddingBottom}>
            They {isSuper ? 'can' : 'cannot'} access the Clinic Admin Portal to change patient
            statuses.
          </p>
        </div>

        <div className="buttons">
          <Button onClick={handleCloseEdit} disabled={loadingDemote || loadingPromote}>
            Cancel
          </Button>

          {isSuper ? (
            <Button
              onClick={handleDemote}
              variant="dark"
              isLoading={loadingDemote || loadingPromote}
              disabled={loadingDemote || loadingPromote}
              style={{ width: 'auto' }}>
              Revoke access to Clinic Admin Portal
            </Button>
          ) : (
            <Button
              onClick={handlePromote}
              variant="dark"
              isLoading={loadingDemote || loadingPromote}
              disabled={loadingDemote || loadingPromote}
              style={{ width: 'auto' }}>
              Grant access to Clinic Admin Portal
            </Button>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Clinician;
